/**
 * Building level names, compared across the two spellings the platform
 * uses (twin slice 1, W4 skeleton).
 *
 * THE TWO SPELLINGS. The RMF building map, the api-server, the 2D map and
 * every locate link name a floor the way the map does: `L9`, `L4M`,
 * `L12`. Some artefacts zero-pad the number instead: the twin's SLAM grids
 * are `NMH_L09.png`, and the edge's clean regions are `L04-*`. Both mean
 * the same floor, so a plain `===` between them silently reads "different
 * floor" -- which, for a locate link's `level` hint, lands the person on
 * the wrong floor without a word.
 *
 * WHAT THIS DOES NOT DO. It does not guess at names outside the `L<n>` /
 * `L<n>M` pattern: anything else (a basement, a roof, a lower-case
 * spelling) is passed through untouched and compared as written. Whether
 * any site uses such names is not established.
 *
 * The twin keeps its own copy of the padding rule in
 * `screens/Twin/geometry.ts` (`toZeroPaddedLevel`) for now; this module is
 * the one the Overview, the resolver and the links import, so none of them
 * has to reach into the lazily loaded twin to compare two floors.
 */

const LEVEL_PATTERN = /^L(\d+)(M?)$/;

/** `L9` -> `L09`, `L4M` -> `L04M`; anything else unchanged. */
export function toZeroPaddedLevel(level: string): string {
  const m = LEVEL_PATTERN.exec(level);
  if (!m) return level;
  return `L${(m[1] ?? '').padStart(2, '0')}${m[2] ?? ''}`;
}

/**
 * The map's own spelling: `L09` -> `L9`, `L04M` -> `L4M`, `L00` -> `L0`;
 * anything else unchanged. This is the spelling a locate link carries.
 */
export function canonicalLevel(level: string): string {
  const m = LEVEL_PATTERN.exec(level);
  if (!m) return level;
  // Leading zeros stripped as text, not through Number(): a digit run
  // longer than a double holds would otherwise come back as a different
  // floor.
  return `L${(m[1] ?? '').replace(/^0+(?=\d)/, '')}${m[2] ?? ''}`;
}

/**
 * True when the two names are the same floor in either spelling. A
 * missing or empty name is never the same floor as anything, itself
 * included: "no level" is not a floor to land on.
 */
export function sameLevel(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return canonicalLevel(a) === canonicalLevel(b);
}
